/**
 * T.Z112 — «ПДн»: a CLIENT-SIDE personal-data mask for the rendered UI.
 *
 * OWNER DECISION 2026-09-17 (revised in-flight from the earlier local-only,
 * hostname-gated brief): the mask is a USER-FACING TOGGLE, available in EVERY
 * environment (kind AND prod) to every authenticated session — the header
 * button (`PdnToggle.tsx`) is the only switch. The hostname no longer gates
 * anything; it only picks the DEFAULT: `cb-manager.local` (the demo stand)
 * boots with the mask ON, everywhere else OFF.
 *
 * Inert by default: while the toggle is off there is no observer and no DOM
 * write at all. Nothing leaves the browser — the api, the DB and every export
 * are untouched, so an xlsx downloaded while the mask is on STILL CARRIES REAL
 * DATA (flagged to the owner; see the module's own limitation list).
 *
 * Mechanics: rewrite TEXT NODES of the already-rendered DOM (phones, e-mails,
 * ФИО), blur matching input/textarea values through a CSS class, and keep up
 * with react-query re-renders through one throttled MutationObserver.
 * Re-masking masked text is a no-op (the masks match no pattern), so the
 * observer can never loop on its own writes.
 *
 * T.Z118 (owner-reported the same day, two findings): a rewrite DESTROYS the
 * text it replaces, so every masked node's pre-mask text is recorded first
 * (`originals`, a WeakMap keyed by the node) and handed back by
 * `stopDemoMaskEngine` — toggle-off really reveals, and toggle-on re-masks
 * from the restored original. An external write (react-query re-rendering a
 * row) re-records, so a stale ФИО is never restored over newer content. And
 * coverage of a re-render is now DETERMINISTIC: every mutation batch is passed
 * synchronously, in the observer callback, before paint — the 120 ms trailing
 * pass only batches. The engine also watches the class/style/hidden
 * attributes (a visibility flip used to be invisible to it) and re-passes on
 * focus / becoming visible.
 *
 * T.Z122 (owner-reported from PROD the same evening, `TypeError: Cannot read
 * properties of undefined (reading 'trim')` on every click of the button): the
 * engine is now fail-safe at three layers — the walker filter is the CORRECT
 * `NodeFilter.SHOW_TEXT` (T.Z118 shipped 5 = element|text, which handed
 * elements to a text-masking loop), every entry into the per-node rewrite is
 * gated on «is this actually a text-shaped node», and one poisoned node can
 * only be logged and skipped.
 *
 * T.Z129 (owner-reported from PROD two days later, teachers page: mask ON →
 * ФИО masked → the «Ожидают подтверждения» chip re-renders the roster → REAL
 * ФИО, and they stayed real). T.Z122's fail-open had a hole with exactly this
 * shape: ANY error that escaped the per-node try — the walk loop header
 * (`walker.nextNode()`), `createTreeWalker` itself, the field sweep, the
 * schedulers, the install calls — landed in `engineGuard` /
 * `disableDemoMaskEngine`, which flipped ONE session-long flag and tore the
 * engine down. From then on nothing was masked, every later re-render showed
 * real data, and clicking the button again could NOT bring the engine back
 * (`startDemoMaskEngine` refused while the flag stood) — only a full reload
 * recovered. The durability contract is now:
 *
 *   * a NODE-level error is isolated per node (T.Z122, unchanged) — it costs
 *     that node, never the engine;
 *   * a PASS-level error (the walk broke, the sweep could not start) costs
 *     that pass — logged, and the next batch / the trailing pass retries;
 *     the observer STAYS CONNECTED;
 *   * the only remaining disable is an INSTALL failure (no MutationObserver,
 *     `observe` refusing) — and that flag is SOFT: every
 *     `startDemoMaskEngine` call clears it and retries, so a bad boot costs
 *     nothing the moment the user toggles again. There is no session-long
 *     refusal any more.
 *
 * Coverage of a react-query swap (the chip click) is layered: the synchronous
 * pass in the observer callback (before paint), then a rAF-BOUNDED convergence
 * pass (a node the sync pass skipped as not-yet-visible is re-passed the frame
 * it becomes visible, at most `FRAME_PASSES_PER_BATCH` extra frames), then the
 * 120 ms trailing batch. `maskPass` reports how many nodes it rewrote, which
 * is what the frame pass uses to decide whether one more frame can still find
 * work.
 *
 * T.Z137 (owner-reported from PROD, `/teachers?chip=awaiting`: real ФИО stayed
 * visible with the mask on). The three earlier fixes were all DURABILITY fixes
 * — the ledger, the walker filter, the pass isolation — and the engine was
 * innocent this time too (pinned in a real chromium: the toggle masks the
 * full-ФИО rows, the phones and the e-mails on that very route). What it
 * missed is a CLASS OF TEXT: `maskNames` only recognised runs of 2–3 FULL
 * Cyrillic words, and the «Ожидают подтверждения» bucket is dominated by the
 * SURNAME-AND-INITIALS form the venue-report imports store verbatim
 * («Иванова А. Б.», «ИВАНОВ И.И.» — the same shape the УНТИ/ЕПГУ exports
 * carry, cf. the ingest trap fixtures) plus the initials-first twin
 * («И. И. Иванов»). Those runs matched nothing and stayed readable, so the
 * page read as «the mask does not work». The name run now admits one name
 * word plus 1–2 initials in either order; the SURNAME word is masked, the
 * initials stay as they are — they already reveal at most the one letter the
 * mask's own style («И***») would show, so there is nothing further to hide.
 */

/** `localStorage` key. `'on'` / `'off'`; absent = the per-environment default. */
export const DEMO_MASK_STORAGE_KEY = 'demo-mask';

/** Root class (`<html class="demo-mask">`) that turns the CSS side on. */
export const DEMO_MASK_CLASS = 'demo-mask';

/** The stand that boots with the mask ON (the owner demos there). */
export const DEMO_MASK_DEFAULT_HOST = 'cb-manager.local';

export type DemoMaskMode = 'on' | 'off';

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * The whole switch, in one pure function. An explicit user choice always wins
 * (the old kill-switch semantics survive as `off`); otherwise the demo stand
 * defaults ON and every other host defaults OFF.
 */
export function resolveDemoMaskMode(
  hostname: string,
  stored: string | null | undefined,
): DemoMaskMode {
  if (stored === 'on') return 'on';
  if (stored === 'off') return 'off';
  return isDemoStand(hostname) ? 'on' : 'off';
}

/** True only for `cb-manager.local` itself or a subdomain of it. */
export function isDemoStand(hostname: string): boolean {
  return hostname === DEMO_MASK_DEFAULT_HOST || hostname.endsWith(`.${DEMO_MASK_DEFAULT_HOST}`);
}

// ---------------------------------------------------------------- masking ----

/** Phone shapes: +7(###)###-##-## · +7 ### ###-##-## · 8-###-###-##-## · 89851234567. */
const PHONE_RE = /(?<!\d)(?:\+?7|8)[\s(-]{0,3}\d{3}[\s)-]{0,2}\d{3}[\s-]{0,2}\d{2}[\s-]{0,2}\d{2}(?!\d)/g;

export const PHONE_MASK = '+7 *** ***-**-**';

/** E-mails (no TLD-less local@host — the funnels carry real domains). */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;

export const EMAIL_MASK = '***@***';

/**
 * One «word» of a Cyrillic personal name: Title-case (Иванов, Анна) or an
 * ALL-CAPS row straight out of a DB import (ИВАНОВ). Three letters minimum
 * keeps prepositions («По», «И») and short tokens («ЦА», «ЕВИ») out of a run.
 */
const W = '(?:[А-ЯЁ][а-яё]{2,}|[А-ЯЁ]{3,})';

/** Surname-ish endings: Иванов/Петрова/Ткаченко/Седых… Kept high-precision —
 * «Дворец», «Клуб», «Школа» (venue vocabulary) deliberately do NOT qualify. */
const SURNAME_SUFFIX = /(?:ов|ев|ёв|ин|ын|ова|ева|ёва|ина|ына|ский|цкий|ская|цкая|ской|цкой|енко|ко|юк|ых|их)$/i;

/** Patronymic-ish endings: Иванович/Сергеевна/Ильинична/Оглы/Заде… */
const PATRONYMIC_SUFFIX = /(?:ович|евич|ьевич|овна|евна|ьевна|ична|инична|ич|оглы|кызы|заде)$/i;

/** Institution / place vocabulary: a run containing any of these is a venue or
 * an organisation, never a person — even when its other words decline like a
 * surname («Петровский Парк»). */
const NON_NAME_WORD_RE =
  /^(?:Школа|Академия|Центр|Дворец|Колледж|Гимназия|Лицей|Клуб|Студия|Секция|Кружок|Университет|Институт|Парк|Стадион|Музей|Библиотека|Город|Округ|Улица|Отдел|Кабинет|Список|Учреждение)$/i;

/** A second word in ANOTHER surname/genitive shape («Иванов Иванов», «Иванов
 * Средний») reads as a heading or a wordplay, not as a person's pair. */
const NON_NAME_TAIL_RE = /(?:ов|ев|ёв|ин|ын|их|ых|ий|ый|ой|ое|ее|ие|ия)$/i;

/**
 * T.Z137 — an initials token: one or TWO capital letters each closed by a dot,
 * the space between them optional («И.», «И.И.», «И. И.»). A trailing undotted
 * letter is deliberately NOT an initial («Иванов И. И» degrades to masking the
 * surname only) — requiring the dot keeps dates and abbreviations
 * («14.09.2026», «г. Москва») out of the run.
 */
const INITIALS = '(?:[А-ЯЁ]\\.\\s?){1,2}';

/** A maximal 2–3 word name run, plus (T.Z137) the surname-and-initials twins
 * the venue-report imports store: one name word + 1–2 initials in either
 * order. Start/non-letter, run, then end, punctuation or a space NOT opening
 * another name word (so 4-word headings never yield a 3-word slice of
 * themselves). The full-word alternates try first, so a complete ФИО never
 * degrades to its surname+initials slice. */
const NAME_RUN_RE = new RegExp(
  `(^|[^А-Яа-яЁёA-Za-z])(${W}(?:[ -]${W}){1,2}|${W}\\s${INITIALS}|${INITIALS}\\s${W})(?=$|[^А-Яа-яЁё]|\\s+(?!${W}))`,
  'g',
);

const NAME_WORD_SPLIT = new RegExp(`(${W})([ -]?)`, 'g');

/** One FULL name word (the `W` shape, whole-string) — what an initials run is
 * allowed to hold exactly one of (T.Z137). */
const NAME_WORD_RE = new RegExp(`^(?:${W})$`);

export function maskPhones(text: string): string {
  return text.replace(PHONE_RE, PHONE_MASK);
}

export function maskEmails(text: string): string {
  return text.replace(EMAIL_RE, EMAIL_MASK);
}

function maskNameRun(run: string): string {
  return run.replace(NAME_WORD_SPLIT, (_all, word: string, sep: string) => `${word.charAt(0)}***${sep}`);
}

function suffixAt(words: string[], index: number, re: RegExp): boolean {
  const word = words[index];
  return word !== undefined && re.test(word);
}

/**
 * ФИО → «И***». A run counts as a name only when it LOOKS like one: a pair
 * needs a surname-ish first word AND a second word that is not itself
 * surname/genitive-shaped («Иванов Иван», «НИКИТОВ НИКИТА» — consonant endings
 * included, so non-Russian male names mask too); a triple needs a surname-ish
 * first OR any patronymic-ish word («Иванов Иван Иванович», «Анна Сергеевна
 * Иванова»). A run carrying institution vocabulary («Академия …», «Дом …») is a
 * venue, never a person. Dates, ids, statuses («ПОЛУЧИЛ ССЫЛКУ», «ПРОШЁЛ ЕВИ»)
 * and brand names («Код Будущего», «Школа № 152») stay as they are. Unsure
 * leans towards masking: this is a demo guard, not a name parser.
 *
 * T.Z137 — a run with initials («Иванова А. Б.», «ИВАНОВ И.И.», «И. И. Иванов»)
 * reads as a person when its ONE full word is surname-shaped; the word is what
 * gets masked, the initials stay verbatim (they already show no more than the
 * mask's own first-letter style). «Куратор И. И.» (a role word, not
 * surname-shaped) and initial-less runs keep the older rules.
 */
export function maskNames(text: string): string {
  return text.replace(NAME_RUN_RE, (_all, lead: string, run: string) => {
    const words = run.split(/[ -]/);
    if (words.some((word) => NON_NAME_WORD_RE.test(word))) return _all;
    const nameWords = words.filter((word) => NAME_WORD_RE.test(word));
    const looksLikeName =
      nameWords.length !== words.length // an initials run (T.Z137)…
        ? nameWords.length === 1 && suffixAt(nameWords, 0, SURNAME_SUFFIX)
        : words.length === 3
          ? suffixAt(words, 0, SURNAME_SUFFIX) ||
            suffixAt(words, 1, PATRONYMIC_SUFFIX) ||
            suffixAt(words, 2, PATRONYMIC_SUFFIX)
          : suffixAt(words, 0, SURNAME_SUFFIX) && !suffixAt(words, 1, NON_NAME_TAIL_RE);
    return looksLikeName ? lead + maskNameRun(run) : _all;
  });
}

/** The one entry point the DOM walker uses. Order is irrelevant: the three
 * patterns do not overlap. T.Z122 — a non-string (an `undefined` that slipped
 * past the node gate) masks to the empty string instead of raising: the regex
 * layer is pure and must be total. */
export function maskText(text: string): string {
  if (typeof text !== 'string') return '';
  return maskNames(maskEmails(maskPhones(text)));
}

/** True when a rendered-field VALUE carries something we mask (drives the CSS
 * blur on inputs/textareas — the value itself is never rewritten, so forms
 * still submit what they held). A non-string value (a field without a value
 * getter, the prod crash shape) does not ask for the blur. */
export function inputNeedsMask(value: string): boolean {
  if (typeof value !== 'string') return false;
  return maskText(value) !== value;
}

// ------------------------------------------------------------------ engine ----

/** Applies the visual state to a document root (injectable for the specs). */
export function setDemoMaskClass(
  doc: { documentElement: { classList: { toggle(name: string, on: boolean): void } } },
  on: boolean,
): void {
  doc.documentElement.classList.toggle(DEMO_MASK_CLASS, on);
}

export interface DemoMaskDeps {
  hostname: string;
  storage: StorageLike;
  doc: Parameters<typeof setDemoMaskClass>[0];
}

/** Persists the choice and flips the root class (no engine work — the button
 * layer starts/stops the observer around this). */
export function writeDemoMaskMode(on: boolean, deps: DemoMaskDeps): void {
  try {
    deps.storage.setItem(DEMO_MASK_STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // Private mode / storage disabled: the mask still applies for this session.
  }
  setDemoMaskClass(deps.doc, on);
}

export function toggleDemoMaskMode(current: boolean, deps: DemoMaskDeps): boolean {
  writeDemoMaskMode(!current, deps);
  return !current;
}

function liveDeps(): DemoMaskDeps {
  return { hostname: window.location.hostname, storage: window.localStorage, doc: document };
}

/** Resolved current mode (`localStorage` + the demo-stand default). */
export function readDemoMaskMode(
  deps: { hostname: string; storage: StorageLike } = liveDeps(),
): DemoMaskMode {
  let stored: string | null = null;
  try {
    stored = deps.storage.getItem(DEMO_MASK_STORAGE_KEY);
  } catch {
    stored = null;
  }
  return resolveDemoMaskMode(deps.hostname, stored);
}

const PASS_THROTTLE_MS = 120;

/**
 * T.Z129 — how many EXTRA frame passes one mutation batch may arm after its
 * synchronous pass. The rAF-bounded convergence: content that the sync pass
 * skipped because it was not yet visible (`checkViewport` settles with layout)
 * is re-passed the frame it paints, and a frame pass that rewrote something
 * earns one more frame — bounded, so a page that keeps mutating can never keep
 * the pass looping on its own.
 */
const FRAME_PASSES_PER_BATCH = 2;

/** DOM `Node.TEXT_NODE`, as the literal: the engine's walk helpers must stay
 * runnable under the plain-Node unit runner, which has no DOM globals. */
const TEXT_NODE = 3;

/**
 * `NodeFilter.SHOW_TEXT` — T.Z122: **4**, not 5. T.Z118 shipped the literal 5,
 * which is `SHOW_ELEMENT (1) | SHOW_TEXT (4)`: the walker handed EVERY element
 * node to a text-masking loop, the first one under `<body>` died on
 * `element.data.trim()` (`data` is `undefined` on an element) and one click of
 * the «ПДн» button took the whole SPA down. T.Z112 was safe because it used the
 * real `NodeFilter.SHOW_TEXT` constant AND a `nodeType` guard; the T.Z118
 * rewrite dropped both at once. The platform constant is preferred where it
 * exists, so the literal can never drift from the DOM again; the plain-Node
 * unit runner has no DOM globals and falls back to the literal.
 */
const SHOW_TEXT: number = typeof NodeFilter !== 'undefined' ? NodeFilter.SHOW_TEXT : 4;

/** Structural stand-in for the observer the engine installs — the real
 * `MutationObserver` satisfies it, and so does the unit runner's fake. */
interface EngineObserver {
  observe(target: unknown, options: unknown): void;
  disconnect(): void;
}

/**
 * T.Z129 — the piece of the browser the engine touches beyond the document.
 * Overridable ONLY for the unit specs (`setDemoMaskEngineTestEnv`), which have
 * no DOM globals: a spec installs a fake observer and manual schedulers so the
 * full install → batch → mask → toggle-off cycle is pinned without a browser.
 * App code never passes anything.
 */
export interface DemoMaskEngineTestEnv {
  /** Builds the mutation observer; absent/throwing = an install failure. */
  Observer?: new (onBatch: () => void) => EngineObserver;
  /** The 120 ms trailing batch (a stand-in for `window.setTimeout`). */
  scheduleTrailing?(run: () => void): number;
  cancelTrailing?(handle: number): void;
  /** The rAF convergence pass (a stand-in for `requestAnimationFrame`). */
  scheduleFrame?(run: () => void): number;
  cancelFrame?(handle: number): void;
  /** The focus/visibility listeners (a stand-in for `addEventListener`). */
  addVisibleListener?(run: () => void): void;
  removeVisibleListener?(run: () => void): void;
}

let testEnv: DemoMaskEngineTestEnv | null = null;

/** Unit-spec only: swap the browser glue for fakes. Pass `null` to restore. */
export function setDemoMaskEngineTestEnv(env: DemoMaskEngineTestEnv | null): void {
  testEnv = env;
}

let observer: EngineObserver | null = null;
let passTimer: number | null = null;
let frameTimer: number | null = null;
let framePassesLeft = 0;
let onVisible: (() => void) | null = null;

/**
 * T.Z129 — set ONLY by a failed INSTALL (`startDemoMaskEngine` could not build
 * or attach the observer). Unlike T.Z122's `engineDisabled` this flag never
 * sticks: every `startDemoMaskEngine` call clears it first and retries, so a
 * boot that landed on a hostile DOM costs nothing beyond that boot — the next
 * «ПДн» click re-attempts the install. Errors DURING a run (a pass, a batch)
 * never touch this flag at all: they are logged and left to the next pass.
 */
let installFailed = false;

/**
 * Engine diagnostics. NEVER logs node content: the pass works on the personal
 * data of minors, and a console line must not become a PII leak (repo rule 1).
 */
function logEngineError(where: string, error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  console.error(`[demo-mask] ${where}: ${message}`);
}

/**
 * The originals ledger (T.Z118): the pre-mask text of every node the engine
 * rewrote, keyed BY the node — a `WeakMap`, so a row React unmounts takes its
 * record with it to the GC (it arrives again unmasked and is masked anew).
 * `masked` is what we last wrote: it is how a pass tells its own write apart
 * from an external one (see `maskTextNode`).
 */
const originals = new WeakMap<object, { original: string; masked: string }>();

/** Structural stand-in for a DOM `Text` node — all the per-node helpers touch. */
export interface TextDataHolder {
  data: string;
}

/**
 * T.Z122 — the gate every entry into the per-node rewrite must pass. A text
 * node holds a string; anything else (an ELEMENT the walker was never supposed
 * to yield — the prod crash —, a comment, a `WeakMap`-shaped leftover, plain
 * `undefined`) is rejected here instead of being dereferenced. `nodeType` is
 * checked only when present, so the structural fakes the unit spec drives stay
 * legal.
 */
function isTextNodeHolder(node: unknown): node is TextDataHolder {
  if (node === null || typeof node !== 'object') return false;
  const shape = node as { nodeType?: unknown; data?: unknown };
  if (shape.nodeType !== undefined && shape.nodeType !== TEXT_NODE) return false;
  try {
    return typeof shape.data === 'string';
  } catch {
    // Even READING `data` can throw on a hostile node — that is a skip, not a
    // crash.
    return false;
  }
}

/**
 * Masks ONE text node in place, from its recorded original. The rewrite is
 * destructive by nature, so the pre-mask text is recorded first and handed
 * back by `restoreTextNode` on toggle-off. The rules:
 *
 *  * IDEMPOTENT — a node already holding our mask keeps its original and is
 *    not re-masked (the masks match no pattern, so the observer cannot loop on
 *    its own writes);
 *  * an EXTERNAL write (react-query re-rendering a row in place) re-records:
 *    the node no longer holds what we remember, so its new text IS the
 *    original now — and when that text needs no mask, the stale record is
 *    dropped, never restored over newer content.
 *
 * T.Z122 — a non-text node (or one whose `data` is not a string) is a silent
 * no-op, never a `TypeError`. T.Z129 — returns whether the node was actually
 * REWRITTEN (the frame convergence pass reads it through `maskPass`).
 */
export function maskTextNode(node: TextDataHolder): boolean {
  if (!isTextNodeHolder(node)) return false;
  const data = node.data;
  const record = originals.get(node);
  const external = record !== undefined && data !== record.masked && data !== record.original;
  const original = external || record === undefined ? data : record.original;
  const masked = maskText(original);
  if (masked === data) {
    if (external) originals.delete(node);
    return false; // our own mask is already in, or there is nothing here to hide
  }
  node.data = masked;
  originals.set(node, { original, masked });
  return true;
}

/** Puts a recorded node's original text back and drops the record. False when
 * the node holds nothing we rewrote (never masked, or already restored — or
 * rejected by the `isTextNodeHolder` gate, T.Z122). */
export function restoreTextNode(node: TextDataHolder): boolean {
  if (!isTextNodeHolder(node)) return false;
  const record = originals.get(node);
  if (record === undefined) return false;
  node.data = record.original;
  originals.delete(node);
  return true;
}

function isMaskableParent(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA') return false;
  // Invisible nodes are the react-query prefetch/cache side of the tree — skip.
  // Safe to skip BECAUSE the reveal is observed too: the engine watches the
  // class/style/hidden attributes AND re-passes one frame later (T.Z129), so a
  // skipped node is masked the moment it becomes visible.
  return typeof el.checkVisibility === 'function' ? el.checkVisibility() : true;
}

/** T.Z122 — `parentElement`, read defensively: a node whose accessor itself
 * throws is skipped (read as «no visible parent»), not fatal. */
function parentElementOf(node: TextDataHolder): Element | null {
  try {
    return (node as Text).parentElement ?? null;
  } catch (error) {
    logEngineError('a node hid its parent behind a throwing accessor', error);
    return null;
  }
}

/**
 * T.Z129 — every text node the body currently holds, with the walk itself
 * unable to kill the pass:
 *
 *  * the platform `TreeWalker` (`SHOW_TEXT`) is preferred;
 *  * a walker that breaks MID-pass hands back what it already found — the rest
 *    waits for the next pass instead of taking the engine down;
 *  * a walker that cannot even START falls back to a plain recursive
 *    `childNodes` walk.
 *
 * This closes the T.Z122 gap: `maskPass`'s old loop header called
 * `walker.nextNode()` OUTSIDE the per-node try, so a single throw there (a
 * patched/broken traversal — exactly the «one poisoned edge» class) escaped
 * into `engineGuard` and, under the T.Z122 rules, disabled the whole engine
 * for the session. The walk can now no longer raise at all.
 */
function collectTextNodes(doc: Document): Text[] {
  const root = doc.body;
  if (!root) return [];
  try {
    const walker = doc.createTreeWalker(root, SHOW_TEXT);
    const found: Text[] = [];
    for (;;) {
      let next: Node | null;
      try {
        next = walker.nextNode();
      } catch (error) {
        logEngineError('the walker broke mid-pass; what it found so far is kept', error);
        return found;
      }
      if (next === null) return found;
      found.push(next as Text);
    }
  } catch (error) {
    logEngineError('the walker could not start; the recursive walk takes over', error);
  }
  return recursiveTextNodes(root);
}

/** The no-TreeWalker fallback: a plain `childNodes` descent. Guarded — a DOM
 * whose `childNodes` itself misbehaves costs the walk, not the engine. */
function recursiveTextNodes(root: Node): Text[] {
  const found: Text[] = [];
  const visit = (node: Node): void => {
    let children: NodeList;
    try {
      children = node.childNodes;
    } catch (error) {
      logEngineError('a subtree hid its children behind a throwing accessor', error);
      return;
    }
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index];
      if (!child) continue;
      if (child.nodeType === TEXT_NODE) found.push(child as Text);
      else visit(child);
    }
  };
  try {
    visit(root);
  } catch (error) {
    logEngineError('the fallback walk stopped early', error);
  }
  return found;
}

/**
 * One CONVERGENCE pass: walk every text node of the body and mask the real
 * ones. Idempotent by construction (`maskTextNode`), so it can run after ANY
 * DOM change and terminates on its own output. Exported (not just engine
 * private) because the unit runner has no DOM: the spec drives it against a
 * fake document to pin the re-render convergence.
 *
 * Returns the number of nodes REWRITTEN — the frame convergence pass uses it
 * to decide whether one more frame can still find work.
 *
 * Fault isolation (T.Z122 + T.Z129): a node that is not text, or one node
 * raising, is logged and skipped; the walk itself cannot raise (see
 * `collectTextNodes`); the field sweep is guarded as a whole and per field.
 * NOTHING in here can reach the engine's install state any more.
 */
export function maskPass(doc: Document): number {
  let rewritten = 0;
  for (const node of collectTextNodes(doc)) {
    try {
      if (!isTextNodeHolder(node)) continue;
      if (!node.data.trim()) continue;
      if (!isMaskableParent(parentElementOf(node))) continue;
      if (maskTextNode(node)) rewritten += 1;
    } catch (error) {
      logEngineError('a node the pass could not process was skipped', error);
    }
  }
  let fields: { forEach(run: (field: HTMLInputElement | HTMLTextAreaElement) => void): void } | null = null;
  try {
    fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  } catch (error) {
    logEngineError('the field sweep could not start', error);
  }
  fields?.forEach((field) => {
    try {
      field.classList.toggle('demo-mask-target', inputNeedsMask(field.value));
    } catch (error) {
      logEngineError('a field the pass could not process was skipped', error);
    }
  });
  return rewritten;
}

/** Walks the same body tree `maskPass` walked and hands every recorded node
 * its text back — the toggle-off half of the toggle (T.Z118). Fault-isolated
 * per node (T.Z122), and the walk itself cannot raise (T.Z129). */
function restorePass(doc: Document): void {
  for (const node of collectTextNodes(doc)) {
    try {
      restoreTextNode(node as Text);
    } catch (error) {
      logEngineError('a node the restore could not process was skipped', error);
    }
  }
}

/** The pass, under the guard: a pass that STILL raises (nothing left in here
 * should) is logged and answered with a trailing retry — never with a
 * teardown. */
function runMaskPass(doc: Document): number {
  try {
    return maskPass(doc);
  } catch (error) {
    logEngineError('a pass failed; the next batch or the trailing pass retries', error);
    scheduleTrailing(doc);
    return 0;
  }
}

function cancelTrailingHandle(handle: number): void {
  try {
    (testEnv?.cancelTrailing ?? ((id: number) => window.clearTimeout(id)))(handle);
  } catch (error) {
    logEngineError('the trailing pass could not be cancelled', error);
  }
}

function cancelFrameHandle(handle: number): void {
  try {
    (testEnv?.cancelFrame ?? ((id: number) => window.cancelAnimationFrame(id)))(handle);
  } catch (error) {
    logEngineError('the frame pass could not be cancelled', error);
  }
}

function scheduleTrailing(doc: Document): void {
  // Batching ONLY. The synchronous pass in the observer callback has already
  // covered everything committed so far; this trailing pass is the safety net
  // for content revealed between batches (and the retry a failed pass asks
  // for). It must never be a node's FIRST chance — deferring the first chance
  // is what let the re-rendered teachers rows through on prod (T.Z118).
  if (passTimer !== null) return;
  try {
    const schedule = testEnv?.scheduleTrailing ?? ((run: () => void) => window.setTimeout(run, PASS_THROTTLE_MS));
    passTimer = schedule(() => {
      passTimer = null;
      if (observer) runMaskPass(doc);
    });
  } catch (error) {
    passTimer = null;
    logEngineError('the trailing pass could not be scheduled', error);
  }
}

function scheduleFramePass(doc: Document): void {
  if (frameTimer !== null) return;
  const schedule = testEnv?.scheduleFrame;
  if (!schedule && typeof requestAnimationFrame === 'undefined') return; // no frames here
  try {
    frameTimer = (schedule ?? ((run: () => void) => requestAnimationFrame(run)))(() => {
      frameTimer = null;
      if (!observer) return;
      if (runMaskPass(doc) > 0 && framePassesLeft > 0) {
        framePassesLeft -= 1;
        scheduleFramePass(doc); // the bounded convergence: one frame found work, try one more
      }
    });
  } catch (error) {
    frameTimer = null;
    logEngineError('the frame pass could not be scheduled', error);
  }
}

function onMutations(doc: Document): void {
  runMaskPass(doc); // same frame: the MutationObserver callback runs before paint
  framePassesLeft = FRAME_PASSES_PER_BATCH - 1;
  scheduleFramePass(doc); // T.Z129 — the convergence pass, after layout settles
  scheduleTrailing(doc);
}

/**
 * T.Z129 — the INSTALL failure boundary (the only remaining disable). The mask
 * is a demo nicety on top of a WORKING page: when the engine cannot even be
 * built or attached, this switches it off for NOW — observer gone, passes
 * cancelled, root class dropped, best-effort restore — instead of throwing out
 * of a React event handler and unmounting the whole SPA (the 2026-09-17 prod
 * crash). The difference from T.Z122's version: the flag is SOFT. The next
 * `startDemoMaskEngine` — the user's next «ПДн» click — clears it and retries.
 */
function failInstall(doc: Document, cause: unknown): void {
  installFailed = true;
  disconnectEngine(doc);
  try {
    setDemoMaskClass(doc, false);
  } catch (error) {
    logEngineError('the root class could not be dropped', error);
  }
  try {
    restorePass(doc);
    doc.querySelectorAll('.demo-mask-target').forEach((el) => el.classList.remove('demo-mask-target'));
  } catch {
    // Even the restore is best-effort here: we are shutting down BECAUSE the
    // DOM turned hostile. Remaining masked text is the least-bad outcome — and
    // the ledger keeps every original for the next successful pass/restore.
  }
  logEngineError('the engine could not start; the next «ПДн» click retries (fail-open)', cause);
}

/**
 * T.Z129 — the mid-run guard. An error during a batch or a visibility pass is
 * LOGGED, the observer STAYS CONNECTED, and a trailing pass retries: a broken
 * pass costs that pass, never the session. (Under T.Z122 this landed in
 * `disableDemoMaskEngine` — one flaky edge error and the mask was gone for
 * good, which is the owner's teachers-page report.)
 */
function engineGuard(doc: Document, step: () => void): void {
  try {
    step();
  } catch (error) {
    logEngineError('an unexpected engine error; the observer stays up and retries', error);
    scheduleTrailing(doc);
  }
}

/** True once the last INSTALL attempt failed — the engine is down until the
 * next start, which retries (T.Z129: never for the rest of the session). */
export function isDemoMaskEngineDisabled(): boolean {
  return installFailed;
}

/** Test-only: the install flag and the observer/scheduler state are module
 * state; a spec resets them between cases. Never call from app code. */
export function resetDemoMaskEngineForTests(): void {
  installFailed = false;
  observer = null;
  passTimer = null;
  frameTimer = null;
  framePassesLeft = 0;
  onVisible = null;
}

export function startDemoMaskEngine(doc: Document = document): void {
  if (observer || !doc.body) return;
  installFailed = false; // T.Z129 — a previous failure never refuses a retry
  try {
    runMaskPass(doc);
    const ObserverCtor = testEnv?.Observer ?? defaultObserverCtor();
    if (!ObserverCtor) throw new Error('MutationObserver is not available in this environment');
    const created = new ObserverCtor(() => {
      engineGuard(doc, () => onMutations(doc));
    });
    created.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    observer = created; // only after observe() succeeded — no half-installed engine
    attachVisibleListener(doc);
  } catch (error) {
    failInstall(doc, error);
  }
}

function defaultObserverCtor(): (new (onBatch: () => void) => EngineObserver) | undefined {
  return typeof MutationObserver !== 'undefined' ? MutationObserver : undefined;
}

/** Content swapped or revealed while the tab was hidden gets one more pass on
 * the way back in. Best-effort: a host without window/listeners (the unit
 * runner) logs and runs without the extra passes. */
function attachVisibleListener(doc: Document): void {
  const handleVisible = () => {
    if (!doc.hidden) engineGuard(doc, () => runMaskPass(doc));
  };
  try {
    (testEnv?.addVisibleListener ?? ((run: () => void) => {
      doc.addEventListener('visibilitychange', run);
      window.addEventListener('focus', run);
    }))(handleVisible);
    onVisible = handleVisible;
  } catch (error) {
    onVisible = null;
    logEngineError('the visibility listener could not be attached', error);
  }
}

/** Takes the observer, the trailing and frame passes and the visibility
 * listeners down. Split out of `stopDemoMaskEngine` so the fail-open path can
 * reuse the exact teardown — each step guarded, because a teardown may be
 * running precisely because something already went wrong (T.Z122). */
function disconnectEngine(doc: Document): void {
  if (observer !== null) {
    try {
      observer.disconnect();
    } catch (error) {
      logEngineError('the observer refused to disconnect', error);
    }
    observer = null;
  }
  if (passTimer !== null) {
    cancelTrailingHandle(passTimer);
    passTimer = null;
  }
  if (frameTimer !== null) {
    cancelFrameHandle(frameTimer);
    frameTimer = null;
  }
  framePassesLeft = 0;
  const visible = onVisible;
  if (visible !== null) {
    try {
      (testEnv?.removeVisibleListener ?? ((run: () => void) => {
        doc.removeEventListener('visibilitychange', run);
        window.removeEventListener('focus', run);
      }))(visible);
    } catch (error) {
      logEngineError('the visibility listeners could not be removed', error);
    }
    onVisible = null;
  }
}

export function stopDemoMaskEngine(doc: Document = document): void {
  // Disconnect FIRST: the restore below writes into the very nodes the observer
  // watches, and any pass it could schedule has nothing left to do.
  disconnectEngine(doc);
  try {
    restorePass(doc);
    doc.querySelectorAll('.demo-mask-target').forEach((el) => el.classList.remove('demo-mask-target'));
  } catch (error) {
    // T.Z129 — a restore that dies must not take the toggle-off click (or the
    // session) with it: the engine is already off the DOM, the originals stay
    // in the ledger, the next toggle-off retries them and the next toggle-on
    // re-masks from them. Nothing is disabled.
    logEngineError('the toggle-off restore failed; the next toggle retries it', error);
  }
}

/** The button's handler: flips the stored mode, the root class and the engine
 * in one move; returns the new state for the component. T.Z122 — every step is
 * guarded, so the click can no longer end as an uncaught `TypeError`. */
export function toggleDemoMask(current: boolean, deps: DemoMaskDeps = liveDeps()): boolean {
  const next = !current; // == toggleDemoMaskMode's return
  try {
    toggleDemoMaskMode(current, deps);
  } catch (error) {
    // A failing write (storage already swallowed, this is the class flip) must
    // not turn the click into a crash; the engine steps decide what runs.
    logEngineError('the mask mode could not be persisted', error);
  }
  if (next) startDemoMaskEngine(deps.doc as Document);
  else stopDemoMaskEngine(deps.doc as Document);
  return next;
}

/** Boot hook (`main.tsx`, after the router mounts): resolve the stored/default
 * state, paint the root class and — when on — install the observer.
 * T.Z122 — boot is wrapped in the fail-open guard too: an error here must leave
 * an UNMASKED, working app, never a blank page at load time. T.Z129 — a failed
 * boot installs nothing permanently: the flag is the soft one, and the next
 * «ПДн» click retries the whole install. */
export function bootDemoMask(deps: DemoMaskDeps = liveDeps()): void {
  const doc = deps.doc as Document;
  try {
    const on = readDemoMaskMode(deps) === 'on';
    setDemoMaskClass(deps.doc, on);
    if (on) startDemoMaskEngine(doc);
  } catch (error) {
    failInstall(doc, error);
  }
}
